import React, { useEffect, useState } from "react";
import { Collapse, Card, Spin, Alert, Table, Tooltip } from "antd";
import { info, error as logAppInsightError } from '../../utils/logger';
import { logError } from "../../utils/logError";

const { Panel } = Collapse;
const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;
const ConditionSnap = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [errorType, setErrorType] = useState(null);
    const [loanID, setLoanID] = useState();
    const [loadingKeys, setLoadingKeys] = useState([]);

    useEffect(() => {
        if (window.elli && window.elli.script && window.elli.script.guest) {
            window.elli.script.guest.create();
            initializeAuthObject();
        } else {
            console.warn("elli.script.guest is not available.");
        }
    }, []);

    const initializeAuthObject = async () => {
        try {
            window.elli.script.getObject("loan").then(async function (loanObject) {
                loanObject.all().then(async function (loan) {
                    setLoanID(loan?.loan?.id);
                    try {
                        const tpoAuthObject = await window.elli.script.getObject("auth");
                        const authCode = await tpoAuthObject.createAuthCode();
                        const url = `${API_DOMAIN}/api/conditions/condition-snapshot`;
                        const response = await fetch(url, {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                            },
                            body: JSON.stringify({
                                authCode: authCode,
                                resourceId: loan?.loan?.id
                            })
                        });

                        if (!response.ok) {
                            const errorBody = await response.json();
                            setLoading(false);
                            setError(`${errorBody.message}`);
                            setErrorType(errorBody.errorType);
                            return;
                        }
                        const result = await response.json();
                        setData(result);
                        setLoading(false);
                    } catch (err) {
                        setError(err.message || "Failed to fetch snapshot");
                        setErrorType("Error");
                    }
                    finally {
                        setLoading(false);
                    }
                });
            });
        } catch (error) {
            const errorCode = logError("CONDITION_SNAPHOT_ERROR");
            logAppInsightError("Condition Snapshot Initialization Failed", {
                component: "Condition Snapshot",
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - Failed to load Condition Snapshot Information.`
            });
        }
    };

    const handleExpand = async (expanded, key) => {
        if (expanded.length == 0) return;
        setLoadingKeys((prev) => [...prev, key]);
        setError(null);
        setErrorType(null);
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const authCode = await tpoAuthObject.createAuthCode();
            const url = `${API_DOMAIN}/api/conditions/condition-currentstatus`;
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    authCode: authCode,
                    resourceId: loanID
                })
            });

            if (!response.ok) {
                const errorBody = await response.json();
                setLoading(false);
                setError(`${errorBody.message}`);
                setErrorType(errorBody.errorType);
                return;
            }
            const result = await response.json();
            setData(result);
        } catch (error) {
            setError(error.message || "Unexpected error occurred");
            setErrorType("Error");
        } finally {
            setLoadingKeys((prev) => prev.filter((k) => k !== key));
        }
    };

    const getStatusStyle = (record) => {
        const fulfilledDate = new Date(record.fulfilledDate);
        const statusDate = new Date();

        if (!fulfilledDate || !statusDate) {
            return { color: "black", fontWeight: "normal" }; // fallback
        }

        const hoursDiff = Math.abs(statusDate - fulfilledDate) / 36e5;
        if (hoursDiff) {
            // 1. If there is a change (any hours) → Green
            if (record.status !== record.currentStatus && record.currentStatus) {
                return { color: "green", fontWeight: "bold" };
            }

            // 2. No change & after 24 hrs → Red
            if (record.status === record.currentStatus && record.currentStatus && hoursDiff > 24) {
                return { color: "red", fontWeight: "bold" };
            }

            // 3. Else (no change but within 24 hrs) → Black
            return { color: "black", fontWeight: "normal" };
        }
    };

    const getAlertType = (type) => {
        if (type === "Info") return "info";
        return "error";
    };

    return (
        <>
            {loading ? (
                <div className="flex justify-center items-center h-40">
                    <Spin size="large" />
                </div>
            ) : error ? (
                <div className="flex items-center justify-center min-h-screen bg-gray-50 p-6">
                    <div className="bg-white shadow-md rounded-lg p-8 max-w-lg text-center border border-gray-200 relative -mt-20">
                        {/* Error Icon */}

                        {errorType == "Error" &&
                            <div className="flex justify-center mb-4">
                                <svg
                                    className="w-12 h-12 text-red-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L4.34 16c-.77 1.333.192 3 1.732 3z"
                                    />
                                </svg>
                            </div>
                        }

                        {errorType == "Info" &&
                            <div className="flex justify-center mb-4">
                                <svg
                                    className="w-12 h-12 text-blue-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"
                                    />
                                </svg>
                            </div>
                        }

                        <h2 className="text-xl font-semibold text-gray-800 mb-3"></h2>
                        <p className="text-gray-600">
                            {error}
                        </p>
                    </div>
                </div>
            ) : data && data.length > 0 ? (
                <Collapse defaultActiveKey={[]} onChange={(key) => handleExpand(key)}>
                    {data.map((item, index) => {
                        const columns = [
                            { title: "Condition ID", dataIndex: "externalId", key: "externalId" },
                            { title: "Title", dataIndex: "title", key: "title" },
                            {
                                title: "Description",
                                dataIndex: "internalDescription",
                                key: "internalDescription",
                                render: (text) => {
                                    if (!text) return "";
                                    const isLong = text.length > 100;
                                    const displayText = isLong
                                        ? text.substring(0, 100) + "..."
                                        : text;
                                    return isLong ? (
                                        <Tooltip
                                            title={text}
                                            overlayStyle={{ maxWidth: 500, whiteSpace: "normal" }}
                                        >
                                            <span>{displayText}</span>
                                        </Tooltip>
                                    ) : (
                                        <span>{displayText}</span>
                                    );
                                },
                            },
                            {
                                title: "Fulfilled Date",
                                dataIndex: "fulfilledDate",
                                key: "fulfilledDate",
                                render: (val, record) => (
                                    <span style={getStatusStyle(record)}>
                                        {new Date(record.fulfilledDate).toLocaleString()}
                                    </span>
                                ),
                            },
                            {
                                title: "Current Status",
                                dataIndex: "currentStatus",
                                key: "currentStatus",
                                render: (text, record) => (
                                    <span style={getStatusStyle(record)}>
                                        {record.currentStatus}
                                    </span>
                                ),
                            },
                            {
                                title: "Status Date",
                                dataIndex: "statusDate",
                                key: "statusDate",
                                render: (val, record) => (
                                    <span style={getStatusStyle(record)}>
                                        {new Date(record.statusDate).toLocaleString()}
                                    </span>
                                ),
                            },
                            {
                                title: "Disposition",
                                dataIndex: "statusOpen",
                                key: "statusOpen",
                                render: (val) => (val ? "Open" : "Close"),
                            },
                        ];

                        return (
                            <Panel
                                key={index}
                                header={`Condition Notified by Broker for review on (${new Date(
                                    item.createdDate
                                ).toLocaleString()})`}
                                className="text-lm text-gray-600"
                            >
                                {item.conditions && item.conditions.length > 0 ? (
                                    <Table
                                        rowKey="_id"
                                        columns={columns}
                                        dataSource={item.conditions}
                                        pagination={false}
                                        size="small"
                                        bordered
                                        className="text-lm text-gray-600"
                                    />
                                ) : (
                                    <p className="text-gray-500">No conditions available</p>
                                )}
                            </Panel>
                        );
                    })}
                </Collapse>
            ) : (
                <div className="text-center text-gray-600">No logs found.</div>
            )}
        </>
    );

};

export default ConditionSnap;
